import { ChevronDown, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useId, useState } from 'react';
import type { BankDayDto } from '@/api/reconciliation';
import { Checkbox } from '@/components/ui/checkbox';
import { formatNetAmount, formatSignedAmount } from '@/lib/format-exact-amount';
import { maskAccountNumber } from '@/lib/mask-account-number';
import { cn, formatCalendarDate } from '@/lib/utils';
import { BankDayStateBadge } from './bank-day-state-badge';

/**
 * Whether one movement matches the bank pane's search.
 *
 * The bank reference is not searchable: #491 stores no column for it because the
 * values are two-character sequence numbers, not references. A type label is
 * absent when the code has no established meaning, so the field is skipped rather
 * than matched on nothing.
 */
const bankTransactionMatches = (
  txn: BankDayDto['transactions'][number],
  needle: string
): boolean => {
  if (!needle) return true;
  return [txn.text, txn.customerReference, txn.typeLabel].some((field) =>
    field ? field.toLowerCase().includes(needle) : false
  );
};

/**
 * One day of the bank's activity.
 *
 * Every day in the chosen range gets a row, including a day the bank never sent.
 * The day carries its state rather than being absent from the list, because an
 * operator has to be able to tell a quiet day from a day whose file never turned
 * up, and a day with no transactions is the normal case: 53 of the 67 sample
 * production files contain none.
 *
 * The row checkboxes are deliberately inert. Selection, the delta arithmetic and
 * the commit action all belong to the matching story; they are rendered disabled
 * here so the row geometry is the one those stories will build on.
 */
export const BankDayGroup: React.FC<{
  day: BankDayDto;
  /**
   * Whether to name the account this day belongs to. The caller decides, because
   * only it knows whether the range covers more than one: naming the account on
   * every row of a single-account range is noise, and leaving it off a range that
   * covers two makes a date ambiguous.
   */
  showAccount?: boolean;
  hideMatched?: boolean;
  search?: string;
}> = ({ day, showAccount = false, hideMatched = false, search = '' }) => {
  // `day.transactions` is the day's true list. Both the search and the hide-matched
  // filter are applied here rather than by the caller so the count and the
  // empty-state wording can tell "the bank sent nothing" apart from "everything
  // here is matched and hidden" or "nothing here matches what you typed".
  const needle = search.trim().toLowerCase();
  const hasTransactions = day.transactions.length > 0;
  const searchMatches = day.transactions.filter((txn) => bankTransactionMatches(txn, needle));
  const shownTransactions = hideMatched
    ? searchMatches.filter((txn) => !txn.matched)
    : searchMatches;
  const allHiddenByFilter = hasTransactions && shownTransactions.length === 0;

  // Expansion is gated on whether the day has anything to show, not on whether it
  // has movements. A quiet day that parsed cleanly still reports its opening and
  // closing figures, and those render inside the panel; gating on transactions
  // alone put them behind a control that could not be operated. It opens by
  // default because on such a day the balances are the only thing it carries.
  // A day whose file never arrived or could not be read has no balances either,
  // so it stays unexpandable.
  const canExpand = hasTransactions || day.balances.length > 0;
  const [expanded, setExpanded] = useState(canExpand);
  const panelId = useId();

  const balances = day.balances.filter((b) => b.amount !== null);
  const absentBalances = day.balances.filter((b) => b.amount === null);

  return (
    <div className="border-b border-border last:border-b-0">
      <h3 className="m-0">
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={panelId}
          disabled={!canExpand}
          className={cn(
            'flex w-full items-center justify-between gap-3 bg-muted/40 px-4 py-2.5 text-left transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
            canExpand ? 'cursor-pointer hover:bg-muted/60' : 'cursor-default'
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {canExpand ? (
              expanded ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )
            ) : (
              <span className="h-3.5 w-3.5 shrink-0" />
            )}
            <span className="truncate font-semibold text-[13px] text-foreground">
              {formatCalendarDate(day.date)}
            </span>
            {showAccount && (
              <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                <span className="sr-only">Account </span>
                {maskAccountNumber(day.accountNumber)}
              </span>
            )}
            {/* A count and a net are only stated for a day whose file arrived and
                parsed. For the other two states there is no figure to report, and
                printing "0 entries / Net $0.00" would assert a quiet day when what
                we actually have is no information. Absent stays absent. */}
            {day.state === 'present' && (
              <span className="shrink-0 text-[11px] text-muted-foreground">
                {day.transactions.length === 1 ? '1 entry' : `${day.transactions.length} entries`}
              </span>
            )}
            <BankDayStateBadge state={day.state} />
          </span>
          {day.state === 'present' && (
            <span className="flex shrink-0 items-center gap-3 font-mono text-[11px] tabular-nums">
              <span className="text-muted-foreground">Net</span>
              <span className="font-semibold text-foreground">{formatNetAmount(day.dayTotal)}</span>
            </span>
          )}
        </button>
      </h3>

      <div id={panelId} hidden={!expanded}>
        {balances.length > 0 && (
          <dl className="flex flex-wrap gap-x-6 gap-y-1 border-t border-border/50 bg-background px-4 py-2 font-mono text-[10px]">
            {balances.map((balance) => (
              <div key={balance.typeCode} className="flex gap-1.5">
                {/* No label means CASS has not told us what the code means. The
                    code itself is the honest fallback; an empty <dt> is not. */}
                <dt className="text-muted-foreground">{balance.label ?? balance.typeCode}</dt>
                <dd className="m-0 tabular-nums text-foreground">
                  {formatNetAmount(balance.amount as string)}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {absentBalances.length > 0 && (
          <p className="m-0 border-t border-border/50 px-4 py-2 text-[11px] text-muted-foreground">
            {/* Not zero. This account never reports a balance in any file, so the
                figure is not in the feed at all. */}
            This account does not report a balance.
          </p>
        )}

        {shownTransactions.length > 0
          ? shownTransactions.map((txn) => (
              <div
                key={txn.id}
                data-matched={txn.matched ? 'true' : undefined}
                className={cn(
                  'flex items-center gap-3 border-t border-border/50 py-2 pr-4 pl-8',
                  txn.matched && 'opacity-40'
                )}
              >
                {/* See the note in ledger-entry-row.tsx: dimming is not a state a
                    screen reader can reach, so the word is stated too. */}
                {txn.matched && <span className="sr-only">Matched</span>}
                <Checkbox
                  checked={false}
                  disabled
                  aria-label={`Select ${txn.text}`}
                  className="shrink-0 rounded-[4px] border-border"
                />
                <div className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-[13px] text-foreground">
                    {txn.text}
                  </span>
                  <span className="mt-0.5 block truncate font-mono text-[10px] text-muted-foreground">
                    Ref {txn.customerReference} &middot; BAI {txn.typeCode}
                    {txn.typeLabel ? <> &middot; {txn.typeLabel}</> : null}
                  </span>
                </div>
                <span
                  className={cn(
                    'shrink-0 font-mono font-medium text-[13px] tabular-nums',
                    txn.direction === 'credit' && 'text-credit',
                    txn.direction === 'debit' && 'text-debit',
                    // Neither. Colouring it as a debit would assert a direction
                    // the file does not carry.
                    txn.direction === null && 'text-muted-foreground'
                  )}
                >
                  {formatSignedAmount(txn.amount, txn.direction, day.currency)}
                </span>
              </div>
            ))
          : null}
      </div>

      {allHiddenByFilter && (
        <p className="m-0 border-t border-border/50 px-4 py-2 pl-8 text-[11px] text-muted-foreground">
          {/* The day is not quiet. Its rows were removed by a filter the operator
              set, which is a different fact, and the wording names the filter that
              did it rather than blaming the day. */}
          {searchMatches.length === 0
            ? 'No transactions on this day match the search.'
            : hideMatched && needle
              ? 'Every transaction on this day matching the search is matched and hidden.'
              : 'All transactions on this day are matched and hidden.'}
        </p>
      )}

      {!hasTransactions && (
        <p className="m-0 border-t border-border/50 px-4 py-2 pl-8 text-[11px] text-muted-foreground">
          {/* A quiet day is a result, not an error. Some days are quiet because of
              the deliberate float, and much of the daily job is checking whether
              that has changed. */}
          {day.state === 'present'
            ? 'No transactions on this day.'
            : day.state === 'not_delivered'
              ? 'No file arrived for this day, so there is nothing to show yet.'
              : 'The file for this day could not be read, so its contents are unknown.'}
        </p>
      )}
    </div>
  );
};
